import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ShieldCheck, ShieldWarning, Warning, Plus, Trash, Siren,
  DoorOpen, Eye, Fire, Drop, X, Bell, Lock, LockOpen
} from '@phosphor-icons/react';

const MODES = [
  { id: 'disarmed', label: 'Disarmed', color: '#737373', desc: 'All sensors deactivated' },
  { id: 'home', label: 'Home', color: '#22C55E', desc: 'Perimeter armed, motion off' },
  { id: 'away', label: 'Away', color: '#FF6B35', desc: 'All sensors fully armed' },
  { id: 'night', label: 'Night', color: '#A855F7', desc: 'Perimeter + ground floor armed' },
  { id: 'vacation', label: 'Vacation', color: '#EF4444', desc: 'Maximum security, all alerts' },
];
const SENSOR_TYPES = ["door", "window", "motion", "smoke", "water_leak", "glass_break", "camera"];
const SENSOR_ICONS = { door: DoorOpen, window: DoorOpen, motion: Eye, smoke: Fire, water_leak: Drop, glass_break: Warning, camera: Eye };

export default function SecurityPage() {
  const { api, user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'door', room: '', zone: 'main' });

  const fetchData = async () => {
    try {
      const [dashRes, sensRes, evtRes] = await Promise.all([
        api.get('/security/dashboard'),
        api.get('/security/sensors'),
        api.get('/security/events?limit=20')
      ]);
      setDashboard(dashRes.data);
      setSensors(sensRes.data);
      setEvents(evtRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [api]);

  const setMode = async (mode) => {
    try {
      await api.put('/security/mode', { mode });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const addSensor = async () => {
    if (!form.name) return;
    try { await api.post('/security/sensors', form); setShowAdd(false); setForm({ name: '', type: 'door', room: '', zone: 'main' }); fetchData(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const deleteSensor = async (id) => {
    if (!window.confirm('Remove this sensor?')) return;
    try { await api.delete(`/security/sensors/${id}`); fetchData(); } catch (e) { console.error(e); }
  };

  const triggerSensor = async (id) => {
    try {
      const res = await api.post(`/security/sensors/${id}/trigger`);
      alert(res.data.alert ? 'ALERT: Security breach detected!' : 'Sensor triggered (system disarmed)');
      fetchData();
    } catch (e) { console.error(e); }
  };

  const panic = async () => {
    if (!window.confirm('ACTIVATE PANIC BUTTON? This will arm all sensors and send emergency alerts.')) return;
    try { await api.post('/security/panic'); fetchData(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  const currentMode = MODES.find(m => m.id === dashboard?.mode) || MODES[0];

  return (
    <div data-testid="security-page" className="space-y-4 sm:space-y-6 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0 hidden sm:block" />
      <div className="glow-orb-teal top-40 right-0 z-0 hidden sm:block" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">Home Protection</p>
          <h1 className="font-heading text-xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Security System</h1>
        </div>
        <button onClick={panic} data-testid="panic-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-500 transition-colors animate-pulse">
          <Siren size={16} weight="fill" /> PANIC
        </button>
      </div>

      {/* Mode Selector */}
      <div className="glass-card p-5 relative z-10 animate-fade-up" data-testid="security-modes" style={{ animationDelay: '100ms' }}>
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} weight="duotone" style={{ color: currentMode.color }} />
          <span className="overline">Security Mode</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${currentMode.color}15`, color: currentMode.color }}>
            {currentMode.label}
          </span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
              className={`p-3 rounded-xl text-center transition-all ${dashboard?.mode === m.id ? 'ring-2' : 'bg-white/5 hover:bg-white/10'}`}
              style={dashboard?.mode === m.id ? { background: `${m.color}15`, borderColor: m.color, ringColor: m.color } : {}}>
              <div className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center" style={{ backgroundColor: `${m.color}20` }}>
                {m.id === 'disarmed' ? <LockOpen size={16} style={{ color: m.color }} /> : <Lock size={16} style={{ color: m.color }} />}
              </div>
              <p className="text-xs font-semibold text-white">{m.label}</p>
              <p className="text-[9px] text-neutral-500">{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 relative z-10 animate-fade-up" style={{ animationDelay: '200ms' }}>
        <div className="glass-card p-4" data-testid="stat-total-sensors">
          <p className="overline mb-1">Sensors</p>
          <p className="text-xl font-heading font-bold text-white">{dashboard?.total_sensors || 0}</p>
        </div>
        <div className="glass-card p-4">
          <p className="overline mb-1">Online</p>
          <p className="text-xl font-heading font-bold text-green-400">{dashboard?.online_sensors || 0}</p>
        </div>
        <div className="glass-card p-4">
          <p className="overline mb-1">Armed</p>
          <p className="text-xl font-heading font-bold text-[#FF6B35]">{dashboard?.armed_sensors || 0}</p>
        </div>
        <div className="glass-card p-4">
          <p className="overline mb-1">Triggered</p>
          <p className="text-xl font-heading font-bold text-red-400">{dashboard?.triggered_sensors || 0}</p>
        </div>
        <div className="glass-card p-4">
          <p className="overline mb-1">Low Battery</p>
          <p className="text-xl font-heading font-bold text-yellow-400">{dashboard?.low_battery || 0}</p>
        </div>
      </div>

      {/* Sensors */}
      <div className="relative z-10 animate-fade-up" style={{ animationDelay: '300ms' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="overline flex items-center gap-2"><Eye size={14} className="text-[#00E5FF]" /> Sensors</h2>
          {user?.role === 'admin' && (
            <button onClick={() => setShowAdd(true)} data-testid="add-sensor-btn"
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 text-xs text-neutral-300 hover:bg-white/10">
              <Plus size={12} /> Add Sensor
            </button>
          )}
        </div>
        {sensors.length === 0 ? (
          <div className="glass-card p-8 text-center text-neutral-500 text-sm">No sensors configured. Add your first sensor.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sensors.map((s, idx) => {
              const Icon = SENSOR_ICONS[s.type] || Eye;
              return (
                <div key={s.sensor_id} className={`glass-card p-4 ${s.triggered ? 'ring-1 ring-red-500/50' : ''}`} data-testid={`sensor-${s.sensor_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon size={18} weight="duotone" className={s.triggered ? 'text-red-400' : s.armed ? 'text-[#FF6B35]' : 'text-neutral-500'} />
                      <div>
                        <p className="text-sm font-semibold text-white">{s.name}</p>
                        <p className="text-[10px] text-neutral-500">{s.room} - {s.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {s.triggered && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">ALERT</span>}
                      {s.armed && !s.triggered && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">Armed</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => triggerSensor(s.sensor_id)} data-testid={`trigger-sensor-${s.sensor_id}`}
                      className="flex-1 text-[10px] py-1.5 rounded-lg bg-white/5 text-neutral-400 hover:bg-white/10">Test</button>
                    {user?.role === 'admin' && (
                      <button onClick={() => deleteSensor(s.sensor_id)} className="p-1.5 rounded-lg text-neutral-600 hover:text-red-400 hover:bg-red-500/10"><Trash size={12} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Event Log */}
      <div className="glass-card p-5 relative z-10 animate-fade-up" data-testid="security-events" style={{ animationDelay: '400ms' }}>
        <h2 className="overline flex items-center gap-2 mb-4"><Bell size={14} className="text-[#F59E0B]" /> Security Events</h2>
        {events.length === 0 ? (
          <p className="text-neutral-500 text-sm text-center py-4">No security events</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/[0.02] text-xs">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${e.severity === 'critical' ? 'bg-red-500 animate-pulse' : e.severity === 'warning' ? 'bg-yellow-500' : 'bg-green-500'}`} />
                <span className="text-white flex-1">{e.message}</span>
                <span className="text-neutral-600 flex-shrink-0">{new Date(e.timestamp).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Sensor Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="glass-card w-full max-w-sm p-6 animate-fade-up" data-testid="add-sensor-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white">Add Sensor</h3>
              <button onClick={() => setShowAdd(false)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Sensor Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="sensor-name" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                data-testid="sensor-type" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                {SENSOR_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
              <input type="text" placeholder="Room" value={form.room} onChange={e => setForm(f => ({ ...f, room: e.target.value }))}
                data-testid="sensor-room" className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            </div>
            <button onClick={addSensor} data-testid="save-sensor"
              className="w-full mt-4 py-2.5 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow">Save Sensor</button>
          </div>
        </div>
      )}
    </div>
  );
}
